/**
 * Build a deterministic, keyword-rich prose paragraph of a tender's key facts
 * from the structured fields we always have (title, region, budget, category,
 * deadline, publication date, document count). Used two ways:
 *  - as the SEO meta description + a page fallback so EVERY tender page has
 *    substantial, tender-specific text even when it has no AI summary / source
 *    description yet (Tier-2 completeness §8 #5);
 *  - grammar stays trivial — each fact is its own short localized sentence,
 *    joined with spaces, so no case/agreement logic is needed across uz/ru.
 *
 * Every fragment is skipped when its value is missing, so a bare tender still
 * yields a valid (shorter) paragraph.
 */
export interface TenderFactsInput {
  title: string;
  /** Localized region name (already resolved). */
  region?: string | null;
  /** Formatted budget (e.g. "350 000 000"); omit the unit — added by the phrase. */
  budget?: string | null;
  /** Localized category names. */
  categories?: string[];
  /** Formatted deadline date. */
  deadline?: string | null;
  /** Formatted publication date. */
  published?: string | null;
  /** Number of attached documents. */
  docCount?: number;
}

type Translate = (key: string, params?: Record<string, string | number>) => string;

export function buildTenderFacts(input: TenderFactsInput, t: Translate): string {
  const parts: string[] = [t('tender.facts.lead', { title: input.title })];

  if (input.region?.trim()) parts.push(t('tender.facts.region', { region: input.region.trim() }));
  if (input.budget?.trim()) parts.push(t('tender.facts.budget', { budget: input.budget.trim() }));

  const cats = (input.categories ?? []).map((c) => c.trim()).filter(Boolean);
  if (cats.length > 0) parts.push(t('tender.facts.category', { category: cats.join(', ') }));

  if (input.deadline?.trim())
    parts.push(t('tender.facts.deadline', { deadline: input.deadline.trim() }));
  if (input.published?.trim())
    parts.push(t('tender.facts.published', { published: input.published.trim() }));
  if (input.docCount && input.docCount > 0)
    parts.push(t('tender.facts.docs', { count: input.docCount }));

  return parts.join(' ');
}

/**
 * Word count below which a source description cannot stand on its own as the
 * page's only prose.
 *
 * Measured on 60 live tenders: the source `description` has a MEDIAN of 8 words
 * ("Сегмент — 20 компл."), and 86% are under 40. It is a procurement line item,
 * not a description. Because it is non-empty it still satisfied a truthy check,
 * which suppressed the deterministic facts paragraph below — so the richest
 * free prose on the page was being blocked by an eight-word stub.
 *
 * 40 is deliberately generous: above it, a source description carries real
 * detail worth showing verbatim; below it, `buildTenderFacts` strictly dominates
 * it (it restates the subject AND adds region, budget, category and deadline).
 */
export const MIN_SUBSTANTIAL_DESCRIPTION_WORDS = 40;

/** True when a source description is rich enough to be the page's main prose. */
export function isSubstantialDescription(text: string | null | undefined): boolean {
  if (!text) return false;
  return text.trim().split(/\s+/).filter(Boolean).length >= MIN_SUBSTANTIAL_DESCRIPTION_WORDS;
}
